// TODO ANT-1661 will fix after getting rid of global datalayer
/* eslint-disable max-lines */
import {eventChannel, type SagaIterator, type Emit, type Channel} from 'redux-saga';
import Store from 'store';
import md5 from 'blueimp-md5';
import type {AxiosPromise} from 'axios';
import {pushDataLayer, sendHashedEmailEvent} from '@optimaxdev/analytics/mobile';

import type {VoidFunctionType, ActionType} from 'constants/flow/flowTypes';
import {
    STAY_IN_TOUCH,
    cookieReminderClose,
    cookieActiveSubscription,
    RESPONSE_SUBSCRIBE_FORM,
    CHECKOUT,
} from 'constants/subscription';
import {NEWSLETTER, CUSTOMER} from 'constants/actions';

import {takeEvery, select, put, putResolve, call, all} from 'redux-saga/effects';
import {doTransform} from 'libs/transformers';
import {analytics, sendEventToDataLayerForCustomerData} from 'libs/analytics/analytics';
import * as newsLetter from 'actions/customer/newsLetter';
import * as bronto from 'actions/bronto/bronto';
import {getSubscribedUserRedirectionLink} from 'selectors/customer/customer';
import {setCustomerEmail} from 'reducers/analytics/impactRadius';
import {getCustomerData} from 'actions/customer/customer';

type SubscribeType = {
    email: string,
    code?: number,
    redirect?: string,
};

/**
 * Creates Welcome popup channel
 * @return {Channel} channel creator
 */
export const createWelcomePopupEventChannel = (): Channel =>
    eventChannel((emitter: Emit): VoidFunctionType => {
        const listener = (event: CustomEvent) => {
            emitter(event);
        };

        window.addEventListener('dynamicYield:newsletter', listener);
        return () => {
            window.removeEventListener('dynamicYield:newsletter', listener);
        };
    });

/**
 * Send analytics after subscription.
 * After successful subscription customer's email is stored in localStorage. This data used as flag
 * for analytics sending after page reloading. Also we have to send null values in the same event to prevent
 * some incorrect behavior of analytics and if you want to change it - discuss it with product manager
 * or developer on product owner side.
 */
export function* sendAnalyticsAfterPageReload(): SagaIterator {
    // TODO rework this generator if page reloading is no needed any more
    try {
        const subscriptionEmail = yield call([Store, 'get'], 'subscriptionEmail');

        if (subscriptionEmail) {
            yield call(
                analytics,
                {
                    event: 'email_subscribed',
                    mktg_email: subscriptionEmail,
                    mktg_hashed_email: md5(subscriptionEmail),
                },
                true,
            );

            yield call([Store, 'remove'], 'subscriptionEmail');
        }
    } catch (error) {
        yield call([console, 'error'], error);
    }
}

/**
 * Send analytics after subscription. Email is saving to send analytics after page reloading.
 * @param {string} email - Email
 */
export function* sendAnalytics(email: string): SagaIterator {
    try {
        yield call(pushDataLayer, {pageview: '/popupnl/success'});
        yield call(
            analytics,
            {
                mktg_email: email,
                mktg_hashed_email: md5(email),
            },
            true,
        );

        // TODO remove next line if page reloading is no needed any more
        yield call([Store, 'set'], 'subscriptionEmail', email);
    } catch (error) {
        yield call([console, 'error'], error);
    }
}

/**
 * Reset page with a url which has a coupon query argument
 * @param {string} [passedUrl] - subscribed customer email
 */
export function* resetPageWithCouponUrl(passedUrl: ?string): SagaIterator {
    const url = yield select(getSubscribedUserRedirectionLink);
    yield call([window.location, 'assign'], url || passedUrl);
}

/**
 * Update cookies after customer successful subscribe
 */
export function updateCookies() {
    document.cookie = `${cookieReminderClose}=true;path=/`;
    document.cookie = `${cookieActiveSubscription}=true;path=/`;
}

/**
 * Update local storage after customer successful subscribe
 * @param {string} email - subscribed customer email
 */
export function* updateLocalStorage(email: string): SagaIterator {
    yield call([Store, 'set'], 'newsletterPopupShown', 'no');
    yield call([Store, 'set'], 'joinClub', true);
    yield call([Store, 'set'], 'subscribeMessage', true);
    yield call([Store, 'set'], 'IRE_TRACK_CONVERSION', false);
    const hash = yield call(doTransform, email, 'sha1');
    yield call([Store, 'set'], 'IRE_EMAIL', hash);
}

/**
 * Sending subscription email data to the Bronto system
 * @param {string} email - Email
 */
export function* brontoSubscribe(email: string): SagaIterator {
    try {
        const action = yield call([bronto, 'subscribe'], email);
        yield putResolve(action);
    } catch (error) {
        yield call([console, 'error'], error);
    }
}
/**
 * Sending subscription email data to the Bronto system
 * @param {string} email - Email
 */
export function* brontoCapture(email: string): SagaIterator {
    try {
        const action = yield call([bronto, 'emailCapture'], email);
        yield putResolve(action);
    } catch (error) {
        yield call([console, 'error'], error);
    }
}

/**
 * Receive subscription email data
 * @param {Object} payload - newsletter information after subscribe
 * @property {string} email - customer email
 * @property {string} from - what form came from
 * @property {string} redirect - redirect path
 */
export function* receiveNewsletterSubscribe(payload: SubscribeType): SagaIterator {
    const action = yield call([newsLetter, 'receiveSubscribe'], payload);
    yield put(action);
}

/**
 * Doing actions after subscription
 * @param {ActionType<AxiosPromise>} action - Action
 */
export function* doAfterSubscribe(action: ActionType<AxiosPromise>): SagaIterator {
    if (action.meta.from === STAY_IN_TOUCH) yield call(updateCookies);
    const {
        meta: {email},
    } = action;
    yield all([
        call(sendHashedEmailEvent, email),
        put(setCustomerEmail(email)),
        putResolve(getCustomerData()),
        call(brontoSubscribe, email),
        call(brontoCapture, email),
        call(sendAnalytics, email),
        call(updateLocalStorage, email),
        call(sendEventToDataLayerForCustomerData, email),
    ]);
    if (action.meta.from !== CHECKOUT) yield call(resetPageWithCouponUrl);
}

/**
 * Saga for subscribe from welcome popup
 * @param {CustomEvent} payload - incoming event from welcome popup
 */
export function* subscribeCustomerSaga(payload): SagaIterator {
    try {
        const {email} = payload;
        yield all([call(receiveNewsletterSubscribe, payload), call(brontoCapture, email)]);
    } catch (error) {
        yield call([console, 'error'], error);
    }
}

/**
 * Send a hashed email to the dataLayer
 * @param {string} email - user email
 */
export function* sendHashedEmailDataToDataLayer(email): SagaIterator {
    yield call(pushDataLayer, {
        mktg_email: email || '',
        mktg_hashed_email: email ? md5(email) : '',
    });
}

/**
 * Handle subscribtion error
 * @param {SubscribeType} data - email data and error code
 */
export function* onSubscribeError(data: SubscribeType): SagaIterator {
    const {email, code} = data;

    if (code === RESPONSE_SUBSCRIBE_FORM.ALREADY) yield call(sendHashedEmailEvent, email);
}

/**
 * Handle subscribtion error action
 * @param {ActionType<AxiosPromise>} action - Action
 */
export function* handleSubscribeError(action: ActionType<AxiosPromise>): SagaIterator {
    const {
        meta: {email},
        payload: {
            response: {
                data: {code},
            },
        },
    } = action;

    yield call(onSubscribeError, {email, code});
}

/**
 * Handle events from welcome popup
 * @param {CustomEvent} event - incoming event from welcome popup
 */
export function* subscribeCustomerByWelcomePopup(event: CustomEvent): SagaIterator {
    switch (event.detail.type) {
        case 'subscribed':
            yield call(subscribeCustomerSaga, event.detail.payload);
            break;
        case 'hashedEmail':
            yield call(sendHashedEmailDataToDataLayer, event.detail.email);
            break;
        case 'onError':
            yield call(onSubscribeError, event.detail.payload);
            break;
        default:
            yield 'no handler';
    }
}

/**
 * Begin of saga
 */
export default function* subscriptionSaga(): SagaIterator {
    const welcomePopupChannel = yield call(createWelcomePopupEventChannel);
    yield takeEvery(`${NEWSLETTER.SUBSCRIBE}_FULFILLED`, doAfterSubscribe);
    yield takeEvery(`${NEWSLETTER.SUBSCRIBE}_REJECTED`, handleSubscribeError);
    yield takeEvery(`${CUSTOMER.GET_CUSTOMER_DATA}_FULFILLED`, sendAnalyticsAfterPageReload);
    yield takeEvery(welcomePopupChannel, subscribeCustomerByWelcomePopup);
}
